import type { OrderStatus } from '@mypoz/shared';
import { STATUS_TONES } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { StatusChip } from '@mypoz/ui';

export interface OrderStatusChipProps {
  /** The lifecycle group, which decides the colour. */
  status: OrderStatus;
  /**
   * The vendor's own words for the exact state — `SubmittedOrder.statusLabel`, already resolved to
   * the reader's language. Optional because a status code missing from the vendor's dictionary
   * has no label of its own; the app's own name for the group stands in then.
   *
   * Not translated here, the same call `PackageStatusChip` makes: since MYP-53 the vendor
   * publishes a label for each of its eighteen statuses, and passing them through says 部分未付款
   * where this app could only ever have said "Unpaid".
   */
  label?: string;
}

/**
 * The order's stage, with a dot that means one of two things: whether you are the one holding it
 * up, or whether the order has stopped.
 *
 * Red on unpaid and shipped — precisely the two states that carry a primary button — and muted
 * everywhere else. Deliberately not a severity ramp across five colours: a customer scanning
 * twenty orders needs to find the two that want something from them, not rank the rest.
 *
 * `exception` is the one addition (MYP-53), and it is the same red `PackageStatusChip` uses for
 * the same reason. A held, problem or modification-requested order carries no button at all, so
 * without a colour it reads as an ordinary row in the middle of the track, which is the app
 * quietly telling the customer something untrue.
 */
export function OrderStatusChip({ status, label }: OrderStatusChipProps) {
  const { t } = useTranslation();

  return (
    <StatusChip tone={STATUS_TONES.order[status]}>
      {label ?? t(`orders.status.${status}`)}
    </StatusChip>
  );
}
